import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { ErrorObject } from '@inmoment/the-kitchen';

import {
  ActionItem,
  ActionPlan,
  ActionPlanService,
  ActionTask,
} from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { Utils } from 'app/utils/utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-create-action-task-dialog',
  templateUrl: './create-action-task-dialog.component.html',
  styleUrls: ['./create-action-task-dialog.component.scss'],
  standalone: false,
})
export class CreateActionTaskDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() actionPlan: ActionPlan;
  @Input() actionItem: ActionItem;
  @Input() actionTask?: ActionTask;
  @Input() fromDashboard = true;

  @Output() create = new EventEmitter<ActionTask>();
  @Output() update = new EventEmitter<ActionTask>();
  @Output() modalClosed = new EventEmitter<void>();

  currentStep = 1;
  isSaving = false;
  form: UntypedFormGroup;
  isEdit = false;

  title = 'Add Task';
  saveButtonText = 'Add Task';

  nameStatus: ErrorObject = ErrorObjectUtils.create();
  dueDateStatus: ErrorObject = ErrorObjectUtils.create();

  minDueDate: Date = new Date();
  maxDueDate: Date = undefined;

  createdTaskName: string;

  constructor(
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private translocoService: TranslocoService,
    formBuilder: UntypedFormBuilder
  ) {
    this.form = formBuilder.group({
      name: [
        '',
        this.validatorFactory(
          this.nameStatus,
          translocoService.translate('create-action-item-dialog-component.name')
        ),
      ],
      dueDate: [
        '',
        this.validatorFactory(
          this.dueDateStatus,
          translocoService.translate('create-action-item-dialog-component.due-date')
        ),
      ],
      assignee: [''],
      note: [''],
    });
  }

  ngOnInit(): void {
    if (this.actionTask) {
      this.isEdit = true;

      this.title = this.translocoService.translate('create-action-task-dialog-component.edit-task');
      this.saveButtonText = this.translocoService.translate(
        'create-action-task-dialog-component.save-task'
      );

      this.form.patchValue({
        name: this.actionTask.name,
        dueDate: new Date(this.actionTask.due_date),
        note: this.actionTask.notes,
        assignee: this.actionTask.assignee_user_name,
      });
    } else {
      this.title = this.translocoService.translate('create-action-task-dialog-component.add-task');
      this.saveButtonText = this.translocoService.translate(
        'create-action-task-dialog-component.add-task'
      );
      this.form.patchValue({
        dueDate: new Date(this.actionItem.due_date),
      });
    }
    this.maxDueDate = new Date(this.actionItem.due_date);
  }

  onCancel(): void {
    this.modalClosed.emit();
  }

  async onAddTask(): Promise<void> {
    this.isSaving = true;

    this.form.disable();

    if (this.isEdit) {
      this.actionTask.name = this.form.controls.name.value;
      this.actionTask.due_date = Utils.getDateIsoString(this.form.controls.dueDate.value);
      this.actionTask.assignee_user_name = this.form.controls.assignee.value;
      this.actionTask.notes = this.form.controls.note.value;

      try {
        const updatedActionTask = await this.actionPlanService.updateActionTask(
          this.actionPlan.plan_id,
          this.actionItem.item_id,
          this.actionTask
        );

        this.update.emit(updatedActionTask);

        this.currentStep = 2;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-task-dialog-component.failed-to-update'),
          error
        );
      }
    } else {
      const actionTask = ActionPlanService.createActionTaskObject({
        name: this.form.controls.name.value,
        dueDate: Utils.getDateIsoString(this.form.controls.dueDate.value),
        assignee: this.form.controls.assignee.value,
        notes: this.form.controls.note.value,
      });

      this.createdTaskName = this.form.controls.name.value;

      try {
        const createdActionTask = await this.actionPlanService.createActionTask(
          this.actionPlan.plan_id,
          this.actionItem.item_id,
          actionTask
        );

        this.create.emit(createdActionTask);

        this.currentStep = 2;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-task-dialog-component.failed-to-create'),
          error
        );
      }
    }

    this.form.enable();

    this.isSaving = false;
  }

  onAddAnotherTask(): void {
    this.form.reset({
      dueDate: new Date(this.actionItem.due_date),
    });

    this.currentStep = 1;
  }

  private validatorFactory(status: ErrorObject, fieldName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        if (control.touched || !control.pristine) {
          ErrorObjectUtils.setError(
            status,
            this.translocoService.translate(
              'create-action-task-dialog-component.fieldname-required',
              { fieldName }
            )
          );
        }

        return { required: true };
      }

      ErrorObjectUtils.setNoError(status);

      return null;
    };
  }
}
